/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Serializes a Date object to an ISO 8601 string.
 *
 * @param date - The date to format
 * @returns The ISO 8601 representation of the date
 */
function formatDateObject(date: Date): string {
  return date.toISOString();
}

/**
 * Parses a date string and re-serializes it to ISO 8601, returning null for
 * unparseable input.
 *
 * @param dateString - The candidate date string
 * @returns The ISO 8601 string, or null when the input is not a valid date
 */
function formatDateString(dateString: string): string | null {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return null;
  }
  return date.toISOString();
}

/**
 * Tests whether a value is a `YYYY-MM-DD` date string.
 *
 * @param value - The value to test
 * @returns True when the value is a string matching the ISO date pattern
 */
function isDateString(value: any): boolean {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * formatDateValue function.
 */
function formatDateValue(value: any): string | null {
  if (value instanceof Date) {
    return formatDateObject(value);
  }

  if (isDateString(value)) {
    return formatDateString(value);
  }

  return null;
}

/**
 * isDateStringAttempted function.
 */
function isDateStringAttempted(value: any): boolean {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

/**
 * formatNumberValue function.
 */
function formatNumberValue(value: number): string {
  return String(value);
}

/**
 * Formats a single array element: recurses into plain objects, stringifies
 * numbers, and passes other primitives through unchanged.
 *
 * @param item - The array element to format
 * @param formatFormValuesFn - Recursive formatter applied to nested objects
 * @returns The formatted element
 */
function formatArrayItem(
  item: any,
  formatFormValuesFn: (formValues: Record<string, any>) => Record<string, any>
): any {
  if (typeof item === "object" && !Array.isArray(item)) {
    return formatFormValuesFn(item);
  }
  if (typeof item === "number") {
    return formatNumberValue(item);
  }
  return item;
}

/**
 * Decides whether an array item should be retained after formatting, dropping
 * empty plain objects while keeping all other values.
 *
 * @param item - The formatted item to test
 * @returns True when the item is a non-empty object or a non-object value
 */
function isValidItem(item: any): boolean {
  if (typeof item === "object" && !Array.isArray(item)) {
    return Object.keys(item).length > 0;
  }
  return true;
}

/**
 * formatArrayValue function.
 */
function formatArrayValue(
  value: any[],
  formatFormValuesFn: (formValues: Record<string, any>) => Record<string, any>
): any[] | null {
  if (value.length === 0) {
    return null;
  }

  const filteredArray = value
    .filter((item) => item !== null && item !== undefined && item !== "")
    .map((item) => formatArrayItem(item, formatFormValuesFn))
    .filter((item) => isValidItem(item));

  return filteredArray.length > 0 ? filteredArray : null;
}

/**
 * formatObjectValue function.
 */
function formatObjectValue(
  value: Record<string, any>,
  formatFormValuesFn: (formValues: Record<string, any>) => Record<string, any>
): Record<string, any> | null {
  const formatted = formatFormValuesFn(value);
  return Object.keys(formatted).length > 0 ? formatted : null;
}

/**
 * Tests whether a value is considered empty (null, undefined, or empty string).
 *
 * @param value - The value to test
 * @returns True when the value is null, undefined, or an empty string
 */
function isEmptyValue(value: any): boolean {
  return value === null || value === undefined || value === "";
}

/**
 * Attempts to format a value as a date, distinguishing "formatted date",
 * "invalid date string", and "not a date" outcomes.
 *
 * @param value - The value to attempt to format as a date
 * @returns The formatted date string, null for an invalid date string, or
 *   undefined when the value is not a date at all
 */
function tryFormatDate(value: any): any {
  const dateFormatted = formatDateValue(value);
  if (dateFormatted !== null) {
    return dateFormatted;
  }
  if (isDateStringAttempted(value)) {
    return null;
  }
  return undefined; // Not a date
}

/**
 * Formats a non-date value: recurses into plain objects and passes primitives
 * through unchanged.
 *
 * @param value - The value to format
 * @param formatFormValuesFn - Recursive formatter applied to nested objects
 * @returns The formatted value
 */
function formatNonDateValue(
  value: any,
  formatFormValuesFn: (formValues: Record<string, any>) => Record<string, any>
): any {
  if (Array.isArray(value)) {
    return formatArrayValue(value, formatFormValuesFn);
  }

  if (typeof value === "object") {
    return formatObjectValue(value, formatFormValuesFn);
  }

  if (typeof value === "number") {
    return formatNumberValue(value);
  }

  return value;
}

/**
 * formatSingleValue function.
 */
function formatSingleValue(
  value: any,
  formatFormValuesFn: (formValues: Record<string, any>) => Record<string, any>
): any {
  if (isEmptyValue(value)) {
    return null;
  }

  const dateResult = tryFormatDate(value);
  if (dateResult !== undefined) {
    return dateResult;
  }

  return formatNonDateValue(value, formatFormValuesFn);
}

/**
 * formatFormValues function.
 *
 * @param {*} formValues - Parameter description
 *
 * @returns {*} Return value description
 */
export function formatFormValues(
  formValues: Record<string, any>
): Record<string, any> {
  const formattedValues: Record<string, any> = {};

  for (const [key, value] of Object.entries(formValues)) {
    const formatted = formatSingleValue(value, formatFormValues);
    if (formatted !== null) {
      formattedValues[key] = formatted;
    }
  }

  return formattedValues;
}
